/**
 * `/o/$uid` — the ONE canonical object-detail surface.
 *
 * Reads `objects.get` (same projection as the object peek) and renders a full
 * ObjectPage. Sufficient for Agents goal deep links (`/agents/goals/$id` →
 * `/o/<uid>?from=agents`).
 */

import { createRoute } from "@tanstack/react-router";
import {
  Button,
  Card,
  CardContent,
  ObjectKeyFacts,
  type ObjectKeyFact,
  ObjectPage,
  PageHeader,
  RelationshipPanel,
  SectionTitle,
  formatRelativeTime,
} from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import { RouterLink as Link } from "@excellent/ui/router";
import { objects } from "@excellent/ui/api/contracts/objects";
import { useVerificationTopBar } from "../verification/shell";
import { rootRoute } from "./__root";

function kindLabel(kind: string): string {
  if (!kind) return "Object";
  return kind.charAt(0).toUpperCase() + kind.slice(1).replace(/_/g, " ");
}

function ObjectDetailPage() {
  const { uid } = objectRoute.useParams();
  const { data, error, isLoading } = useApi(objects.get, {
    params: { uid },
  });
  const pageTitle = error
    ? "Object unavailable"
    : data?.title ?? (isLoading ? "Loading object" : "Object not found");
  useVerificationTopBar({ detailTitle: pageTitle });

  if (isLoading && !data) {
    return (
      <div className="px-6 py-8">
        <PageHeader title="Loading object" description="Reading this object." />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className="px-6 py-8"
        data-testid="object-not-found"
      >
        <PageHeader
          title={pageTitle}
          description={
            <span className="text-destructive">
              {error?.message ?? "Object not found."}
            </span>
          }
        />
      </div>
    );
  }

  const facts: ObjectKeyFact[] = [];
  if (data.owner) {
    facts.push({
      label: "Owner",
      value: data.owner.name,
      person: {
        name: data.owner.name,
        kind: data.owner.kind === "agent" ? "agent" : "human",
      },
    });
  }
  for (const v of data.vitals ?? []) {
    facts.push({
      label: v.label,
      value: v.value,
      hint: v.hint ?? undefined,
      intent: v.intent ?? undefined,
    });
  }

  const timeline =
    data.events && data.events.length > 0 ? (
      <Card className="gap-0 overflow-hidden py-0">
        <SectionTitle title="Timeline" />
        <CardContent className="flex flex-col gap-2 py-4">
          {data.events.slice(0, 40).map((e) => (
            <div key={e.uid} className="type-body">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-medium text-foreground">{e.label}</span>
                <span className="shrink-0 type-subtext text-muted-foreground">
                  {formatRelativeTime(e.occurredAt)}
                </span>
              </div>
              {e.summary ? (
                <p className="type-subtext text-muted-foreground">{e.summary}</p>
              ) : null}
              {e.actorLabel ? (
                <p className="type-subtext text-faint">{e.actorLabel}</p>
              ) : null}
            </div>
          ))}
        </CardContent>
      </Card>
    ) : undefined;

  return (
    <div className="px-6 py-8" data-testid="object-page">
      <ObjectPage
        title={data.title}
        kind={kindLabel(data.kind)}
        state={data.state}
        subtitle={data.essence ?? undefined}
        keyFacts={facts.length > 0 ? <ObjectKeyFacts facts={facts} /> : undefined}
        summary={
          <div className="flex flex-col gap-3 type-body">
            {data.targetOutcome ? (
              <div>
                <div className="type-subtext uppercase tracking-wide text-faint">
                  Outcome
                </div>
                <p className="text-foreground">{data.targetOutcome}</p>
              </div>
            ) : null}
            {data.description ? (
              <p className="text-muted-foreground whitespace-pre-wrap">
                {data.description}
              </p>
            ) : (
              <p className="text-muted-foreground">
                No description for this {data.kind}.
              </p>
            )}
            {data.fulfills ? (
              <p className="type-subtext text-muted-foreground">
                Fulfills{" "}
                <Link
                  href={data.fulfills.href}
                  className="underline underline-offset-2"
                >
                  {data.fulfills.label}
                </Link>
              </p>
            ) : null}
          </div>
        }
        timeline={timeline}
        actions={
          data.actions && data.actions.length > 0 ? (
            <Card className="gap-0 overflow-hidden py-0">
              <SectionTitle title="Actions" />
              <CardContent className="flex flex-col gap-2 py-4">
                {data.actions.map((a) => (
                  <Button
                    key={a.key}
                    asChild
                    variant={a.variant ?? "outline"}
                    className="w-full justify-center"
                  >
                    <Link href={a.href}>{a.label}</Link>
                  </Button>
                ))}
              </CardContent>
            </Card>
          ) : undefined
        }
        relationships={
          data.relationships && data.relationships.length > 0 ? (
            <RelationshipPanel
              groups={data.relationships.map((g) => ({
                label: g.label,
                items: g.items.map((i) => ({
                  key: i.key,
                  label: i.label,
                  href: i.href ?? undefined,
                })),
              }))}
            />
          ) : undefined
        }
      />
    </div>
  );
}

export const objectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/o/$uid",
  component: ObjectDetailPage,
});
